// polyfill window.getMatchedCSSRules() in FireFox 6+
const ELEMENT_RE = /[\w-]+/g,
  ID_RE = /#[\w-]+/g,
  CLASS_RE = /\.[\w-]+/g,
  ATTR_RE = /\[[^\]]+\]/g,
  // :not() pseudo-class does not add to specificity, but its content does as if it was outside it
  PSEUDO_CLASSES_RE = /:(?!not)[\w-]+(\(.*\))?/g,
  PSEUDO_ELEMENTS_RE = /::?(after|before|first-letter|first-line|selection)/g;

// handles extraction of `cssRules` as an `Array` from a stylesheet or something that behaves the same
function getSheetRules(stylesheet: CSSStyleSheet | CSSMediaRule): CSSRule[] {
  const sheet_media = stylesheet.media && stylesheet.media.mediaText;

  // if this sheet is disabled skip it
  if (stylesheet instanceof CSSStyleSheet && stylesheet.disabled) return [];

  // if this sheet's media is specified and doesn't match the viewport then skip it
  if (
    sheet_media &&
    sheet_media.length &&
    !window.matchMedia(sheet_media).matches
  )
    return [];

  // get the style rules of this sheet
  return Array.from(stylesheet.cssRules);
}

function _find(string: string, re: RegExp) {
  const matches = string.match(re);
  return matches ? matches.length : 0;
}

// calculates the specificity of a given `selector`
function calculateScore(selector: string) {
  const score = [0, 0, 0],
    parts = selector.split(" ");
  let part, match;

  while (((part = parts.shift()), typeof part == "string")) {
    // find all pseudo-elements
    match = _find(part, PSEUDO_ELEMENTS_RE);
    score[2] += match;
    // and remove them
    match && (part = part.replace(PSEUDO_ELEMENTS_RE, ""));
    // find all pseudo-classes
    match = _find(part, PSEUDO_CLASSES_RE);
    score[1] += match;
    // and remove them
    match && (part = part.replace(PSEUDO_CLASSES_RE, ""));
    // find all attributes
    match = _find(part, ATTR_RE);
    score[1] += match;
    // and remove them
    match && (part = part.replace(ATTR_RE, ""));
    // find all IDs
    match = _find(part, ID_RE);
    score[0] += match;
    // and remove them
    match && (part = part.replace(ID_RE, ""));
    // find all classes
    match = _find(part, CLASS_RE);
    score[1] += match;
    // and remove them
    match && (part = part.replace(CLASS_RE, ""));
    // find all elements
    score[2] += _find(part, ELEMENT_RE);
  }
  return parseInt(score.join(""), 10);
}

// returns the heights possible specificity score an element can get from a give rule's selectorText
function getSpecificityScore(element: Element, selector_text: string) {
  const selectors = selector_text.split(",");
  let selector,
    score,
    result = 0;
  while ((selector = selectors.shift())) {
    if (element.matches(selector)) {
      score = calculateScore(selector);
      result = score > result ? score : result;
    }
  }
  return result;
}

function sortBySpecificity(element: Element, rules: CSSStyleRule[]) {
  // comparing function that sorts CSSStyleRules according to specificity of their `selectorText`
  function compareSpecificity(a: CSSStyleRule, b: CSSStyleRule) {
    return (
      getSpecificityScore(element, b.selectorText) -
      getSpecificityScore(element, a.selectorText)
    );
  }

  return rules.sort(compareSpecificity);
}

export const getMatchedCSSRules = function (element: Element) {
  let sheet: CSSStyleSheet | undefined,
    rules: CSSRule[],
    rule: CSSRule | undefined;
  const result = [];

  // get stylesheets and convert to a regular Array
  const styleSheets = Array.from(window.document.styleSheets);

  // assuming the browser hands us stylesheets in order of appearance
  // we iterate them from the beginning to follow proper cascade order
  while ((sheet = styleSheets.shift())) {
    // get the style rules of this sheet
    rules = getSheetRules(sheet);
    // loop the rules in order of appearance
    while ((rule = rules.shift())) {
      // if this is an @import rule
      if (rule instanceof CSSImportRule && rule.styleSheet) {
        // insert the imported stylesheet's rules at the beginning of this stylesheet's rules
        rules = getSheetRules(rule.styleSheet).concat(rules);
        // and skip this rule
        continue;
      }

      // if there's no stylesheet attribute BUT there IS a media attribute it's a media rule
      else if (rule instanceof CSSMediaRule) {
        // insert the contained rules of this media rule to the beginning of this stylesheet's rules
        rules = getSheetRules(rule).concat(rules);
        // and skip it
        continue;
      }

      // "regular" styles are added to the results.
      else if (rule instanceof CSSStyleRule) {
        // check if this element matches this rule's selector
        if (element.matches(rule.selectorText)) {
          // push the rule to the results set
          result.push(rule);
        }
      }
    }
  }
  // sort according to specificity
  return sortBySpecificity(element, result);
};

export function inlineCSSRules(element: Element | HTMLElement) {
  const rules = getMatchedCSSRules(element);
  const elementStyle = getComputedStyle(element);

  if ("style" in element) {
    // inline any css variables in element inline styles
    for (const prop of element.style) {
      const originalValue = element.style.getPropertyValue(prop);
      const value = fillInVariableValue(elementStyle, originalValue);
      if (value != originalValue) {
        element.style.setProperty(prop, value);
      }
    }
  }
  // inline any css variables in element inline styles
  for (const attr of element.attributes) {
    const originalValue = attr.value;
    const value = fillInVariableValue(elementStyle, originalValue);
    const transformedValue = cssHslToRGB(value);
    if (transformedValue != originalValue) {
      element.setAttribute(attr.name, transformedValue);
    }
  }

  const appliedProps = new Set();
  for (const rule of rules) {
    const props = Array.from(rule.style);
    for (const prop of props) {
      if (appliedProps.has(prop)) continue;
      appliedProps.add(prop);

      const value = fillInVariableValue(
        elementStyle,
        rule.style.getPropertyValue(prop)
      );

      // fill and style attributes have to be converted seperately,
      // to increase compatibility with most applications
      if (prop == "fill") {
        element.setAttribute("fill", cssHslToRGB(value));
      } else if (prop == "stroke") {
        element.setAttribute("stroke", cssHslToRGB(value));
      } else if (prop == "stop-color") {
        element.setAttribute("stop-color", cssHslToRGB(value));
      }
      // don't inline transition prop
      else if (prop == "transition") {
        continue;
      }
      // set the transform attribute directly
      else if (prop == "transform") {
        element.setAttribute("transform", value);
      } else if ("style" in element) {
        const currentValue = element.style.getPropertyValue(prop);
        if (!currentValue) {
          element.style.setProperty(prop, value);
        }
        // else {
        //   console.debug(`${prop} already set`, currentValue);
        // }
      }
    }
  }

  const children = Array.from(element.children);
  for (const child of children) {
    inlineCSSRules(child);
  }
}

export function fillInVariableValue(
  elementStyle: CSSStyleDeclaration,
  cssValue: string
): string {
  if (!cssValue) return "";

  // replace "simple" variables first
  // (those with one parameter only)
  // e.g. var(--bg)
  const varRegexSimple = /var\((--[a-zA-Z_][^,)]*)?\)/;
  const replacerSimple = function (
    match: string,
    variable: string,
    _secondPart: string
  ) {
    const variableKey = variable.trim();
    const val = elementStyle.getPropertyValue(variableKey).trim();
    return val || fillInVariableValue(elementStyle, val);
  };
  const replacedSimple = cssValue.replace(varRegexSimple, replacerSimple);

  // replace more complex variables afterwards
  const varRegex = /var\(([^,]*)(, (.*))?\)/;
  const replacer = function (
    match: string,
    variable: string,
    _secondPart: string,
    defaultValue: string
  ) {
    const val = elementStyle.getPropertyValue(variable.trim()).trim();
    return val || fillInVariableValue(elementStyle, defaultValue);
  };
  const replaced = replacedSimple.replace(varRegex, replacer);

  return replaced;
}

export function getConnectedCSSRules(element: Element) {
  let rules = getMatchedCSSRules(element);

  const children = Array.from(element.children);

  for (const child of children) {
    const childRules = getConnectedCSSRules(child);

    rules = concatRules(rules, childRules);
  }

  return rules;
}

function concatRules(rulesA: CSSStyleRule[], rulesB: CSSStyleRule[]) {
  const newRules = rulesA.slice();

  outerLoop: for (const rule of rulesB) {
    for (const ruleA of newRules) {
      if (rule.cssText == ruleA.cssText) continue outerLoop;
    }
    newRules.push(rule);
  }
  return newRules;
}

export function cssHslToRGB(cssValue: string) {
  if (!cssValue.includes("hsl")) return cssValue;
  const hslRegExp = /hsl\((\d+%?), (\d+%?), (\d+%?)\)/;
  const matches = cssValue.match(hslRegExp);
  if (!matches) return cssValue;

  const h = matches[1].includes("%")
    ? cssValueToNumber(matches[1])
    : parseInt(matches[1]) / 360;
  const s = cssValueToNumber(matches[2]);
  const l = cssValueToNumber(matches[3]);
  const [r, g, b] = hslToRgb(h, s, l);
  return cssValue.replace(hslRegExp, `rgb(${r}, ${g}, ${b})`);
}

function cssValueToNumber(percent: string) {
  if (!percent.includes("%")) return parseFloat(percent);
  return parseFloat(percent) / 100;
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */
function hslToRgb(h: number, s: number, l: number) {
  let r, g, b;

  if (s == 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = function hue2rgb(p: number, q: number, t: number) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
